import { useMemo } from "react";
import { Zap, Radio, Eye, Clock, Flame } from "lucide-react";
import {
  useTemporalMode,
  type TemporalMode,
} from "@/lib/sync/offsetOrchestration";
import { useEdgeSafeFlags } from "@/lib/resilience/edgeSafeFlags";

interface Props {
  /** Optional override — when not idle, this is shown as an extra status pill. */
  dominantKind?: "idle" | "heat_window" | "story_arc" | "contrarian" | "viral_card" | "crowd";
  className?: string;
}

const MODE_BADGE: Record<TemporalMode, { label: string; icon: typeof Zap; tone: string }> = {
  ultra_live: { label: "Ultra Live Active", icon: Zap, tone: "text-[oklch(0.82_0.24_145)]" },
  predictive: { label: "Predictive Mode", icon: Eye, tone: "text-primary" },
  balanced: { label: "Fast Feed", icon: Clock, tone: "text-foreground" },
  tv_sync: { label: "TV Sync Mode", icon: Radio, tone: "text-muted-foreground" },
};

/**
 * CompetitiveWatchModeBadge — Phase 4
 * Tiny status pill that surfaces the active temporal mode so users feel the
 * competitive edge ("Ultra Live Active", "Predictive Mode", "Heat Window
 * Critical"). No polling, no state — reads the temporal-mode snapshot.
 */
export function CompetitiveWatchModeBadge({ dominantKind, className }: Props) {
  const { mode } = useTemporalMode();
  const flags = useEdgeSafeFlags();
  const badge = MODE_BADGE[mode];
  const Icon = badge.icon;

  const heatCritical = useMemo(
    () => dominantKind === "heat_window",
    [dominantKind],
  );

  if (flags.screenshotFreezeMode) return null;
  const backdropCls = flags.allowBackdropFX ? "backdrop-blur-md" : "";

  return (
    <div
      className={`pointer-events-none inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${backdropCls} ${className ?? ""}`}
      data-screenshot-target="competitive-watch-mode"
    >
      <Icon className={`h-3 w-3 ${badge.tone}`} />
      <span className={badge.tone}>{badge.label}</span>
      {heatCritical && (
        <>
          <span className="mx-0.5 h-3 w-px bg-border/70" />
          <Flame className="h-3 w-3 text-[oklch(0.65_0.25_25)]" />
          <span className="text-[oklch(0.65_0.25_25)]">Heat Critical</span>
        </>
      )}
    </div>
  );
}
